/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    ADMIN_USERS_UI_SPLIT
 * Date:         2025-12-30
 * Modified By:  Cursor
 *
 * Description:
 * Extracted pagination component from UsersTable.tsx to reduce file size.
 */

import React from "react";
import { Pagination, Form } from "react-bootstrap";

interface UsersTablePaginationProps {
  total: number;
  page: number;
  limit: number;
  onPageChange: (page: number) => void;
  onLimitChange: (limit: number) => void;
  showLimitSelector?: boolean;
}

export const UsersTablePagination: React.FC<UsersTablePaginationProps> = ({
  total,
  page,
  limit,
  onPageChange,
  onLimitChange,
  showLimitSelector = true,
}) => {
  const totalPages = Math.ceil(total / limit);
  const startItem = (page - 1) * limit + 1;
  const endItem = Math.min(page * limit, total);

  if (total === 0) return null;

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mt-3">
        <div className="text-muted small">
          Showing {startItem}-{endItem} of {total} users
        </div>
        {showLimitSelector && (
          <div className="d-flex gap-2 align-items-center">
            <span className="text-muted small">Rows per page:</span>
            <Form.Select
              size="sm"
              value={limit}
              onChange={(e) => onLimitChange(Number(e.target.value))}
              style={{ width: "auto" }}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </Form.Select>
          </div>
        )}
      </div>

      {totalPages > 1 && (
        <div className="d-flex justify-content-center mt-3">
          <Pagination>
            <Pagination.First onClick={() => onPageChange(1)} disabled={page === 1} />
            <Pagination.Prev onClick={() => onPageChange(page - 1)} disabled={page === 1} />
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              const pageNum = Math.max(1, page - 2) + i;
              if (pageNum > totalPages) return null;
              if (pageNum < 1) return null;
              return (
                <Pagination.Item
                  key={pageNum}
                  active={pageNum === page}
                  onClick={() => onPageChange(pageNum)}
                >
                  {pageNum}
                </Pagination.Item>
              );
            })}
            <Pagination.Next
              onClick={() => onPageChange(page + 1)}
              disabled={page === totalPages}
            />
            <Pagination.Last
              onClick={() => onPageChange(totalPages)}
              disabled={page === totalPages}
            />
          </Pagination>
        </div>
      )}
    </>
  );
};
